import { useState } from 'react';
import { Check, Pencil, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAppSelector } from '@/features/hooks';
import type { ReceiptSuggestedCategory } from '@/lib/types';

interface ReceiptCategorySuggestionProps {
  suggestion: ReceiptSuggestedCategory;
  onApply: (category: string) => void;
  applying?: boolean;
}

export default function ReceiptCategorySuggestion({
  suggestion,
  onApply,
  applying,
}: ReceiptCategorySuggestionProps) {
  const { categories } = useAppSelector((s) => s.categories);
  const [editing, setEditing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(suggestion.category);

  const confidenceIcon =
    suggestion.confidence === 'high' ? '🎯' : suggestion.confidence === 'medium' ? '🤔' : '❓';

  return (
    <div
      className="rounded-md border bg-amber-50/80 dark:bg-amber-950/30 p-3"
      data-testid="category-suggestion-banner"
    >
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Suggested category: {confidenceIcon}{' '}
            <Badge variant="secondary">{suggestion.category}</Badge>
            <span className="ml-1 text-xs text-muted-foreground capitalize">
              ({suggestion.confidence} confidence
              {suggestion.reason ? ` — ${suggestion.reason}` : ''})
            </span>
          </p>

          {editing ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="h-9 w-48" data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id || cat.name} value={cat.name}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                className="min-h-[44px]"
                disabled={applying}
                onClick={() => {
                  onApply(selectedCategory);
                  setEditing(false);
                }}
                data-testid="category-apply-edit"
              >
                <Check className="mr-1 h-3 w-3" /> Apply
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                className="min-h-[44px]"
                disabled={applying}
                onClick={() => onApply(suggestion.category)}
                data-testid="category-apply"
              >
                <Check className="mr-1 h-3 w-3" /> Apply
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="min-h-[44px]"
                onClick={() => setEditing(true)}
                data-testid="category-edit"
              >
                <Pencil className="mr-1 h-3 w-3" /> Edit
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
